import { Autocomplete, AutocompleteChangeReason, AutocompleteChangeDetails, TextField, Typography, SxProps, InputProps as StandardInputProps, autocompleteClasses, useTheme } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';

export interface MultiChoicesAutocompleteProps<T> {
  options: T[];
  label: string;
  onChange: (values: T[], reason: AutocompleteChangeReason, details?: AutocompleteChangeDetails<T>) => void;
  values?: T[];
  // Should be provided when T is not string
  getOptionLabel?: (option: T) => string;
  isOptionEqualToValue?: (option: T, value: T) => boolean;
  groupBy?: (option: T) => string;
  InputProps?: Partial<StandardInputProps>;
  sx?: SxProps;
  disabled?: boolean;
  limitTags?: number;
}

export default function MultiChoicesAutocomplete<T = string>(
  { options, label, onChange, isOptionEqualToValue, values, getOptionLabel, groupBy, InputProps, sx, disabled, limitTags }: MultiChoicesAutocompleteProps<T>,
): React.JSX.Element {
  const [localValues, setLocalValues] = useState<T[]>([]);
  const globalTheme = useTheme();

  const handleOnChange = useCallback((newValues: T[], reason: AutocompleteChangeReason, details?: AutocompleteChangeDetails<T>) => {
    setLocalValues(newValues);
    onChange(newValues, reason, details);
  }, [onChange]);

  useEffect((): void => {
    setLocalValues(values || []);
  }, [values]);

  return (
    <Autocomplete
      multiple
      limitTags={limitTags}
      options={options}
      disabled={disabled}
      disableCloseOnSelect
      isOptionEqualToValue={isOptionEqualToValue}
      getOptionLabel={getOptionLabel}
      groupBy={groupBy}
      onChange={(event, newValues, reason, details) => handleOnChange(newValues, reason, details)}
      renderOption={(props, option, { }) => {
        const { key, ...renderProps } = props;

        return (
          <li
            key={key}
            {...renderProps}
          >
            <Typography
              variant="bodyMedium"
              color="secondary"
            >
              {typeof option === 'string' ? option : getOptionLabel!(option)}
            </Typography>
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          slotProps={{
            ...params.slotProps,
            input: {
              ...params.slotProps.input,
              ...InputProps,
              startAdornment: (
                <>
                  {InputProps?.startAdornment}
                  {params.slotProps.input.startAdornment}
                </>
              ),
            },
          }}
          onKeyDown={(event) => {
            if (event.key !== 'Escape') {
              event.stopPropagation();
            }
          }}
          placeholder={!localValues.length ? label : undefined}
          sx={{
            '& .MuiInputLabel-root, & .MuiInputBase-input': {
              fontSize: '1rem',
              top: 3,
            },
          }}
        />
      )}
      size="small"
      slotProps={{
        popper: {
          sx: {
            zIndex: globalTheme.zIndex.snackbar - 1,
          },
        },
        paper: groupBy ? {
          sx: {
            [`& .${autocompleteClasses.groupLabel}`]: {
              typography: 'labelMedium',
              lineHeight: '48px',
            },
          },
        } : undefined,
      }}
      sx={{
        width: {
          xs: '100%',
          sm: 268,
        },
        '& .MuiAutocomplete-tag': {
          maxWidth: 'calc(100% - 32px)',
        },
        ...sx,
      }}
      value={values}
    />
  );
}
